<template>
    <ComponentBanner
        v-if="offering"
        position="footer"
        :lead="offering.strings.offerLead"
        :text="offering.strings.offer"
        :cta-label="offering.strings.offerCta"
        :secondary-label="offering.strings.offerSkip"
        :auto-dismiss-ms="5000"
        :dismissible="false"
        cta-dusk="startFeatureTour"
        secondary-dusk="skipFeatureTour"
        @cta="accept"
        @secondary="decline"
        @dismiss="ignore"
    />
</template>

<script>
import { mapState } from 'vuex';
import ComponentBanner from '_shared_/UI/Components/ComponentBanner.vue';
import { createTourEngine } from './tourEngine';
import { runTour } from './tourRunner';
import { TOURS, countTourIgnored, hasTourFlag, markTourFlag, tourIgnoredCount } from './tourRegistry';
import { tourStrings } from './tourStrings';

// The locale keys every tour's popover buttons read, under the tour's own section.
const stringsFor = (localizations, lang, tour) => tourStrings(localizations, lang)[tour.stringsKey] ?? {};

// A banner that closes by itself was not answered. After this many of them the
// next visit shows the tour instead of asking, so it is seen or declined, never
// merely missed.
const AUTO_START_AFTER_IGNORED = 2;

export default {
    name: 'FeatureTours',
    components: { ComponentBanner },
    data() {
        return {
            offering: null,
            running: null,
            // One offer per visit: closing and reopening the panel does not ask again.
            offeredThisVisit: false,
        };
    },
    computed: {
        ...mapState(['ui', 'tours', 'localizations', 'lang']),
        /**
         * The first registered tour whose situation holds right now, or null.
         * The reader's own tour comes first: both offers share the footer, and
         * that one is offered once per device, so covering it would lose it.
         */
        offerable() {
            const { state, getters } = this.$store;
            if (state.tourBanner.opened || state.onboarding.hasToBeShown) return null;
            return Object.values(TOURS).find(tour => tour.offerWhen(state, getters)) ?? null;
        },
    },
    watch: {
        offerable: {
            immediate: true,
            handler(tour) {
                // An offer left standing after its situation ended would start
                // the tour over whatever the reader went on to do.
                if (this.offering && this.offering.tour !== tour) this.offering = null;
                if (!tour || this.running || this.offeredThisVisit) return;
                if (hasTourFlag(tour, 'answered') || hasTourFlag(tour, 'completed')) return;
                this.offeredThisVisit = true;
                if (tourIgnoredCount(tour) >= AUTO_START_AFTER_IGNORED) {
                    markTourFlag(tour, 'answered');
                    this.start(tour.id);
                    return;
                }
                this.offering = { tour, strings: stringsFor(this.localizations, this.lang, tour) };
            },
        },
        'tours.requested'(id) {
            if (!id) return;
            this.$store.commit('clearTourRequest');
            this.start(id);
        },
    },
    beforeDestroy() {
        this.running?.stop();
    },
    methods: {
        accept() {
            const { tour } = this.offering;
            if (this.offerable !== tour) {
                this.offering = null;
                return;
            }
            markTourFlag(tour, 'answered');
            this.start(tour.id);
        },
        decline() {
            markTourFlag(this.offering.tour, 'answered');
            this.offering = null;
        },
        ignore() {
            countTourIgnored(this.offering.tour);
            this.offering = null;
        },
        start(id) {
            const tour = TOURS[id];
            if (!tour || !tour.isAvailable(this.$store.getters)) return;

            this.offering = null;
            this.running?.stop();
            const strings = stringsFor(this.localizations, this.lang, tour);
            this.running = runTour(tour.script(this.$store.getters, strings), {
                store: this.$store,
                engine: createTourEngine(),
                labels: {
                    next: strings.next,
                    done: strings.done,
                    skip: strings.skip,
                    close: strings.close,
                    progress: strings.progress,
                },
                onEnd: outcome => {
                    this.running = null;
                    if (outcome === 'completed') markTourFlag(tour, 'completed');
                    // The popover held the focus and has gone: it returns to
                    // the replay control, or to the panel's first action.
                    this.$nextTick(() => {
                        const target =
                            document.querySelector('[dusk="AddOnsTourButton"]') ??
                            document.querySelector('[dusk="AddOnsCreateButton"]');
                        target?.focus();
                    });
                },
            });
        },
    },
};
</script>
